import { Box, CircularProgress, Typography } from "@mui/material";
import { type ChatMessage } from "@xop/dashboard-shared";
import { useCallback, useEffect, useRef, useState } from "react";

import { ConfirmDialog } from "../../components/ui/confirm-dialog.js";
import { MessageBubble } from "./chat-thread/MessageBubble.js";
import { TypingIndicator } from "./chat-thread/TypingIndicator.js";

/**
 * The WhatsApp-style message list. Own messages are right-aligned green
 * bubbles (with sent/read ticks for DMs); everyone else's are left-aligned
 * neutral bubbles — with the sender's name in the "All Staff" room. A pill
 * day-divider separates calendar days, and the whole thing sits on the chat
 * wallpaper. Auto-scrolls to the newest message as the list grows.
 *
 * This file is the list shell — the wallpaper, the loading / empty states, the
 * one-at-a-time edit + delete state, and the auto-scroll. Each message renders
 * through `chat-thread/MessageBubble`; the live typing bubble through
 * `TypingIndicator`; the per-message reaction pill / delivery caption /
 * desktop "Update now" / Sent-Delivered-Seen logic live alongside the bubble
 * in `chat-thread/`.
 */
export function ChatThread({
  messages,
  selfId,
  loading,
  showSenderNames,
  readUpTo,
  isDm,
  recipientOnline = false,
  typingName = null,
  onReact,
  onEdit,
  onDelete,
  onReply,
}: {
  messages: ChatMessage[];
  selfId: number;
  loading: boolean;
  /** Show the sender's name above others' messages (true for the room). */
  showSenderNames: boolean;
  /** Highest of MY message ids the other party has read (DM read receipts). */
  readUpTo: number;
  /** DMs show ticks; the room doesn't (too many readers). */
  isDm: boolean;
  /** DM only: is the other participant currently online? Drives the
   * "Delivered" middle state of the explicit Sent/Delivered/Seen caption. */
  recipientOnline?: boolean;
  /** Name of whoever is typing in this conversation right now (drives the
   * in-thread animated typing-dots bubble). Null/empty hides it. */
  typingName?: string | null;
  /** Toggle an emoji reaction on a message. Omit to render reactions read-only. */
  onReact?: (messageId: number, emoji: string) => void;
  /** Edit my own message's body. Omit to hide the edit affordance. */
  onEdit?: (messageId: number, body: string) => void;
  /** Delete my own message. Omit to hide the delete affordance. */
  onDelete?: (messageId: number) => void;
  /** Start replying to a message (any message — mine or a teammate's). Omit to
   * hide the Reply affordance. */
  onReply?: (message: ChatMessage) => void;
}): JSX.Element {
  const endRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Inline edit + delete-confirm state (one message at a time).
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState("");
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  // The message a reply quote just jumped to — flashed briefly so the eye
  // catches it. Cleared after the flash (or if another jump happens first).
  const [highlightId, setHighlightId] = useState<number | null>(null);
  // Scroll to + flash the quoted original. A no-op when it's paged out (older
  // than the loaded page) — degrade gracefully rather than fetch on click.
  const jumpToMessage = useCallback((id: number) => {
    const el = containerRef.current?.querySelector(`[data-message-id="${id}"]`);
    if (!el) return;
    el.scrollIntoView({ block: "center" });
    setHighlightId(id);
    window.setTimeout(() => setHighlightId((cur) => (cur === id ? null : cur)), 1600);
  }, []);
  const lastId = messages.at(-1)?.id ?? 0;
  const showTyping = Boolean(typingName);
  // The newest of MY own (non-deleted) DM messages — the only one that carries
  // the explicit Sent/Delivered/Seen caption (like iMessage), so the thread
  // isn't littered with a status line under every bubble.
  const myLastDmId = isDm
    ? messages.reduce((acc, m) => (m.sender_id === selfId && !m.deleted_at ? m.id : acc), 0)
    : 0;
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [lastId, showTyping]);

  const wallpaper = {
    flex: 1,
    overflowY: "auto" as const,
    px: { xs: 1.5, sm: 4 },
    py: 1.5,
    display: "flex",
    flexDirection: "column" as const,
    gap: 0.25,
    bgcolor: (t: import("@mui/material/styles").Theme) => t.palette.chat.wallpaper,
    // A faint dot texture so the wallpaper doesn't read as a flat fill.
    backgroundImage: (t: import("@mui/material/styles").Theme) =>
      `radial-gradient(${t.palette.chat.meta}22 0.5px, transparent 0.5px)`,
    backgroundSize: "18px 18px",
  };

  if (loading) {
    return (
      <Box sx={{ ...wallpaper, alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (messages.length === 0 && !showTyping) {
    return (
      <Box sx={{ ...wallpaper, alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ color: "chat.meta", fontSize: 14 }}>
          No messages yet — say hello.
        </Typography>
      </Box>
    );
  }

  return (
    <Box data-testid="chat-thread" ref={containerRef} sx={wallpaper}>
      {messages.map((m, i) => (
        <MessageBubble
          key={m.id}
          message={m}
          prev={i > 0 ? messages[i - 1] : undefined}
          selfId={selfId}
          showSenderNames={showSenderNames}
          isDm={isDm}
          readUpTo={readUpTo}
          recipientOnline={recipientOnline}
          myLastDmId={myLastDmId}
          editing={editingId === m.id}
          editText={editText}
          highlighted={highlightId === m.id}
          onEditTextChange={setEditText}
          onStartEdit={(msg) => {
            setEditingId(msg.id);
            setEditText(msg.body);
          }}
          onCancelEdit={() => setEditingId(null)}
          onSaveEdit={(id, body) => {
            onEdit?.(id, body);
            setEditingId(null);
          }}
          onRequestDelete={(id) => setPendingDeleteId(id)}
          canReact={Boolean(onReact)}
          canEdit={Boolean(onEdit)}
          canDelete={Boolean(onDelete)}
          canReply={Boolean(onReply)}
          onReact={(id, emoji) => onReact?.(id, emoji)}
          onReply={(msg) => onReply?.(msg)}
          onJumpToMessage={jumpToMessage}
        />
      ))}

      {/* Live typing-dots bubble (auto-expires via the provider's TTL). For the
        * room it's prefixed with who's typing; a DM just shows the dots. */}
      {showTyping && (
        <TypingIndicator showSenderNames={showSenderNames} typingName={typingName} />
      )}
      <div ref={endRef} />

      {/* Destructive delete routes through the shared ConfirmDialog (never
        * window.confirm — repo convention). */}
      <ConfirmDialog
        isOpen={pendingDeleteId !== null}
        onClose={() => setPendingDeleteId(null)}
        title="Delete message?"
        description="This message will be removed for everyone. This can't be undone."
        confirmLabel="Delete"
        onConfirm={() => {
          if (pendingDeleteId !== null) onDelete?.(pendingDeleteId);
          setPendingDeleteId(null);
        }}
      />
    </Box>
  );
}
